import * as Sentry from '@sentry/react';
import { useEffect } from 'react';
import {
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from 'react-router';

import { chunkLoadErrorPattern } from './chunkLoadError';

export const isChunkLoadSentryEvent = (event: Sentry.ErrorEvent) => {
  const message = event.message || '';
  const exceptionValues = event.exception?.values ?? [];
  const hasChunkLoadException = exceptionValues.some((value) => {
    const type = value.type || '';
    const val = value.value || '';
    return chunkLoadErrorPattern.test(type) || chunkLoadErrorPattern.test(val);
  });

  return chunkLoadErrorPattern.test(message) || hasChunkLoadException;
};

const axiosRequestTimeoutPattern = /timeout of \d+ms exceeded/i;

const isAxiosRequestTimeoutFailure = (error: {
  code?: string;
  message?: string;
  name?: string;
}) => {
  if (error.name !== 'AxiosError' || error.code !== 'ECONNABORTED') {
    return false;
  }

  return axiosRequestTimeoutPattern.test(error.message ?? '');
};

/** Axios client-side deadline (e.g. stream-chat default 3000ms), not HTTP 408. */
export const isAxiosRequestTimeout = (error: unknown) => {
  if (!error || typeof error !== 'object') {
    return false;
  }

  return isAxiosRequestTimeoutFailure(
    error as Parameters<typeof isAxiosRequestTimeoutFailure>[0],
  );
};

export const isAxiosRequestTimeoutSentryEvent = (
  event: Sentry.ErrorEvent,
  originalException?: unknown,
) => {
  if (isAxiosRequestTimeout(originalException)) {
    return true;
  }

  const exceptionValues = event.exception?.values ?? [];
  return exceptionValues.some((value) => {
    const type = value.type || '';
    const val = value.value || '';

    return type === 'AxiosError' && axiosRequestTimeoutPattern.test(val);
  });
};

const TRANSIENT_HTTP_STATUSES = new Set([502, 503, 504]);

const axiosTransientServerErrorPattern =
  /request failed with status code (502|503|504)/i;

const isAxiosTransientServerFailure = (error: {
  message?: string;
  name?: string;
  response?: { status?: number };
}) => {
  if (error.name !== 'AxiosError') {
    return false;
  }

  const status = error.response?.status;
  if (status !== undefined && TRANSIENT_HTTP_STATUSES.has(status)) {
    return true;
  }

  return axiosTransientServerErrorPattern.test(error.message ?? '');
};

export const isAxiosTransientServerError = (error: unknown) => {
  if (!error || typeof error !== 'object') {
    return false;
  }

  return isAxiosTransientServerFailure(
    error as Parameters<typeof isAxiosTransientServerFailure>[0],
  );
};

export const isAxiosTransientServerErrorSentryEvent = (
  event: Sentry.ErrorEvent,
  originalException?: unknown,
) => {
  // API gateways return 502/503/504 during deploys, overload, or upstream outages.
  // These are infrastructure issues, not frontend defects.
  if (isAxiosTransientServerError(originalException)) {
    return true;
  }

  const exceptionValues = event.exception?.values ?? [];
  return exceptionValues.some((value) => {
    const type = value.type || '';
    const val = value.value || '';

    return type === 'AxiosError' && axiosTransientServerErrorPattern.test(val);
  });
};

const isRequestAbortedSentryEvent = (event: Sentry.ErrorEvent) => {
  // Treat user-navigation cancellations as expected behavior and drop them from Sentry.
  // Examples:
  // - fetch() / AbortController: DOMException: AbortError
  // - axios: ERR_CANCELED / CanceledError / AxiosCanceledError
  // - app-level messages: "Request aborted"
  const message = (event.message || '').toLowerCase();
  if (message.includes('request aborted')) return true;

  const exceptionValues = event.exception?.values ?? [];
  return exceptionValues.some((value) => {
    const type = (value.type || '').toLowerCase();
    const val = (value.value || '').toLowerCase();

    return (
      type.includes('aborterror') ||
      val.includes('aborterror') ||
      (type.includes('domexception') && val.includes('abort')) ||
      type.includes('cancelederror') ||
      type.includes('axioscancelederror') ||
      val.includes('err_canceled') ||
      val.includes('err_cancele') || // matches both "canceled" and "cancelled"
      val.includes('request aborted')
    );
  });
};

export const isIOSDevice = (ua: string) => {
  // iPadOS can present itself as Macintosh, so we combine UA with touch points.
  const isLikelyIPadOS = /Macintosh/.test(ua) && navigator.maxTouchPoints > 1;

  return /iPad|iPhone|iPod/.test(ua) || isLikelyIPadOS;
};

export const getDetailedDeviceType = () => {
  const ua = navigator.userAgent;

  // 1. Проверка на iOS (iPhone/iPad)
  const isIOS = isIOSDevice(ua);

  if (isIOS) {
    // Если есть тач и это "Mac", значит это iPad
    return ua.includes('iPhone') || ua.includes('iPod') ? 'phone' : 'tablet';
  }

  // 2. Проверка на Android
  if (/Android/i.test(ua)) {
    return /Mobile/i.test(ua) ? 'phone' : 'tablet';
  }

  return 'desktop';
};

export const getOsName = () => {
  const ua = navigator.userAgent;
  const isIOS = isIOSDevice(ua);

  if (isIOS) return 'ios';
  if (/Android/i.test(ua)) return 'android';

  return 'other';
};

export const getDebugContext = () => {
  const nav = navigator as Navigator & {
    connection?: { downlink?: number; effectiveType?: string };
    deviceMemory?: number;
  };

  return {
    screen: {
      width: window.screen.width,
      height: window.screen.height,
      avail_width: window.screen.availWidth,
      avail_height: window.screen.availHeight,
      pixel_ratio: window.devicePixelRatio,
      orientation: screen.orientation ? screen.orientation.type : 'unknown',
    },
    viewport: {
      width: window.innerWidth,
      height: window.innerHeight,
    },
    network: {
      type: nav.connection?.effectiveType || 'unknown',
      downlink: nav.connection?.downlink,
    },
    device_caps: {
      touch_points: navigator.maxTouchPoints,
      memory_gb: nav.deviceMemory || 'unknown',
      cpu_cores: navigator.hardwareConcurrency || 'unknown',
    },
    app_state: {
      is_standalone: window.matchMedia('(display-mode: standalone)').matches,
      referrer: document.referrer || 'direct',
      language: navigator.language,
    },
    env: {
      is_secure_context: window.isSecureContext,
    },
  };
};

export const getDeviceDebugLogAttributes = (options?: {
  ref?: string;
  version?: string;
}) => {
  const pwaMode = window.matchMedia('(display-mode: standalone)').matches
    ? 'standalone'
    : 'browser';

  return {
    ...(options?.version ? { version: options.version } : {}),
    ...(options?.ref ? { ref: options.ref } : {}),
    form_factor: getDetailedDeviceType(),
    is_pwa: window.matchMedia('(display-mode: standalone)').matches,
    os_name: getOsName(),
    pwa_mode: pwaMode,
    touch_points: navigator.maxTouchPoints,
    ...getDebugContext(),
  };
};

type UnhandledRejectionDetail = {
  reason?: unknown;
};

const isEventLike = (value: unknown): value is Event =>
  typeof value === 'object' &&
  value !== null &&
  'type' in value &&
  'target' in value;

const isCustomEventLike = (value: unknown): value is CustomEvent =>
  isEventLike(value) && 'detail' in value;

/** Unwrap Safari / extension wrappers that reject with Event or CustomEvent. */
export const extractUnhandledRejectionReason = (
  originalException: unknown,
): unknown => {
  if (!isEventLike(originalException)) {
    return originalException;
  }

  if (originalException.type !== 'unhandledrejection') {
    return originalException;
  }

  if (isCustomEventLike(originalException)) {
    const detail = originalException.detail as
      | UnhandledRejectionDetail
      | null
      | undefined;

    if (detail && typeof detail === 'object' && 'reason' in detail) {
      return detail.reason;
    }
  }

  if ('reason' in originalException) {
    return (originalException as PromiseRejectionEvent).reason;
  }

  return originalException;
};

const isEmptyObjectRejection = (reason: unknown) => {
  if (reason instanceof Error) {
    return false;
  }

  if (typeof reason !== 'object' || reason === null) {
    return false;
  }

  return Object.keys(reason).length === 0;
};

const isBrowserExtensionRejection = (reason: unknown) => {
  if (!(reason instanceof Error)) {
    return false;
  }

  const stack = reason.stack ?? '';
  return (
    reason.message.includes('Extension context invalidated') ||
    /chrome-extension:|moz-extension:|safari-extension:/.test(stack)
  );
};

export const isOpaqueEventRejectionSentryEvent = (event: Sentry.ErrorEvent) => {
  const exceptionValues = event.exception?.values ?? [];
  return exceptionValues.some((value) => {
    const type = (value.type || '').toLowerCase();
    const val = (value.value || '').toLowerCase();

    return (
      (type === 'customevent' || type === 'event') &&
      val.includes('unhandledrejection') &&
      val.includes('captured as promise rejection')
    );
  });
};

export const isVimeoVideoUnavailableSentryEvent = (
  event: Sentry.ErrorEvent,
) => {
  // Vimeo Player rejects when a lesson/resource URL is deleted, private, or
  // missing the unlisted hash. These are content/config issues, not app bugs.
  const exceptionValues = event.exception?.values ?? [];
  return exceptionValues.some((value) => {
    const type = (value.type || '').toLowerCase();
    const val = (value.value || '').toLowerCase();

    if (type === 'notfounderror' && val.includes('this video does not exist')) {
      return true;
    }

    return (
      val.includes(
        'because of its privacy settings, this video cannot be played here',
      ) ||
      val.includes('is not embeddable') ||
      (val.includes('was not found') && val.includes('vimeo.com'))
    );
  });
};

export const isBenignNonErrorRejectionSentryEvent = (
  event: Sentry.ErrorEvent,
) => {
  // Safari often rejects fetch/blob operations with DOMException objects that
  // have no enumerable keys, producing uninformative Sentry noise.
  const exceptionValues = event.exception?.values ?? [];
  return exceptionValues.some((value) => {
    const type = (value.type || '').toLowerCase();
    const val = (value.value || '').toLowerCase();

    return (
      type === 'unhandledrejection' &&
      val.includes('object captured as promise rejection') &&
      val.includes('[object has no keys]')
    );
  });
};

export const shouldDropUnhandledRejectionEvent = (
  event: Sentry.ErrorEvent,
  originalException?: unknown,
) => {
  if (isBenignNonErrorRejectionSentryEvent(event)) {
    return true;
  }

  if (!isOpaqueEventRejectionSentryEvent(event)) {
    return false;
  }

  const reason = extractUnhandledRejectionReason(originalException);

  if (reason === originalException || reason == null) {
    return true;
  }

  if (isBrowserExtensionRejection(reason)) {
    return true;
  }

  if (isEmptyObjectRejection(reason)) {
    return true;
  }

  if (isAxiosTransientServerError(reason)) {
    return true;
  }

  if (isAxiosRequestTimeout(reason)) {
    return true;
  }

  if (reason instanceof Error) {
    return isRequestAbortedSentryEvent({
      exception: {
        values: [{ type: reason.name, value: reason.message }],
      },
    } as Sentry.ErrorEvent);
  }

  return false;
};

const enrichEventWithRejectionReason = (
  event: Sentry.ErrorEvent,
  reason: unknown,
): Sentry.ErrorEvent => {
  if (!(reason instanceof Error)) {
    return event;
  }

  const mechanism = event.exception?.values?.[0]?.mechanism;

  return {
    ...event,
    exception: {
      values: [
        {
          mechanism,
          type: reason.name,
          value: reason.message,
        },
      ],
    },
  };
};

const sanitizeLogAttributes = (attributes: Record<string, unknown>) => {
  const sanitizedAttributes: Record<string, unknown> = {};

  Object.entries(attributes).forEach(([key, value]) => {
    if (
      /token|authorization|password|secret|cookie/i.test(key) &&
      !/fingerprint|has_active_push_credential|has_push_token/i.test(key)
    ) {
      sanitizedAttributes[key] = '[Filtered]';
      return;
    }

    sanitizedAttributes[key] = value;
  });

  return sanitizedAttributes;
};

const sanitizeSentryLog = <TLog extends { attributes?: unknown }>(
  log: TLog,
) => {
  if (
    !log.attributes ||
    typeof log.attributes !== 'object' ||
    Array.isArray(log.attributes)
  ) {
    return log;
  }

  return {
    ...log,
    attributes: sanitizeLogAttributes(
      log.attributes as Record<string, unknown>,
    ),
  };
};

export const initTracker = () => {
  if (!import.meta.env.PROD) return;

  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    environment: import.meta.env.VITE_ENVIRONMENT,
    integrations: [
      Sentry.reactRouterBrowserTracingIntegration({
        createRoutesFromChildren,
        matchRoutes,
        useEffect,
        useLocation,
        useNavigationType,
      }),
      Sentry.consoleLoggingIntegration({ levels: ['error'] }),
    ],
    release: import.meta.env.VITE_VERSION,
    replaysOnErrorSampleRate: 1.0,
    replaysSessionSampleRate: 0.1,
    tracesSampleRate: 1.0,
    beforeSendLog: sanitizeSentryLog,
    beforeSend: (event, hint) => {
      if (isChunkLoadSentryEvent(event)) {
        return null; // drop expected chunk errors after deployment
      }

      if (isRequestAbortedSentryEvent(event)) {
        return null; // drop expected request cancellations (navigation/AbortController)
      }

      if (isVimeoVideoUnavailableSentryEvent(event)) {
        return null; // drop expected Vimeo playback failures for invalid/private URLs
      }

      if (
        isAxiosTransientServerErrorSentryEvent(event, hint.originalException)
      ) {
        return null; // drop transient gateway/service-unavailable responses
      }

      if (isAxiosRequestTimeoutSentryEvent(event, hint.originalException)) {
        return null; // drop axios client timeouts (slow networks / contended startup)
      }

      if (shouldDropUnhandledRejectionEvent(event, hint.originalException)) {
        return null; // drop Safari / extension opaque promise rejections
      }

      if (isOpaqueEventRejectionSentryEvent(event)) {
        const reason = extractUnhandledRejectionReason(hint.originalException);
        if (reason instanceof Error) {
          return enrichEventWithRejectionReason(event, reason);
        }
      }

      // Ignore errors originating from external domains we don't control
      const blockedDomains =
        /canva\.com|loom\.com|buildasign\.com|onesignal\.com/;
      const frames = event.exception?.values?.[0]?.stacktrace?.frames ?? [];
      const hasBlockedDomain = frames.some(
        (frame) => frame.filename && blockedDomains.test(frame.filename),
      );
      if (hasBlockedDomain) {
        return null; // drop event
      }
      return event;
    },
  });
  // Session Replay is ~120 KB; keep it off the startup bundle and fetch it
  // from Sentry's CDN after init (a static import pulls it into the entry chunk).
  void Sentry.lazyLoadIntegration('replayIntegration')
    .then((replayIntegration) => {
      Sentry.addIntegration(
        replayIntegration({
          maskAllText: false,
          networkDetailAllowUrls: [window.location.origin],
        }),
      );
    })
    .catch(() => {
      // Blocked by an ad blocker or offline: errors are still reported without replays.
    });
  const deviceType = getDetailedDeviceType();
  Sentry.setTag('device.category', deviceType);
  Sentry.setAttribute('device.category', deviceType);
  const pwaMode = window.matchMedia('(display-mode: standalone)').matches
    ? 'standalone'
    : 'browser';
  Sentry.setTag('pwa.mode', pwaMode);
  Sentry.setAttribute('pwa.mode', pwaMode);
  const osName = getOsName();
  Sentry.setTag('os.name', osName);
  Sentry.setAttribute('os.name', osName);
  Sentry.setContext('device_info', {
    form_factor: deviceType,
    os_name: osName,
    is_pwa: pwaMode === 'standalone',
    touch_points: navigator.maxTouchPoints,
  });
  // Keep full runtime diagnostics in a separate context for easier issue triage.
  Sentry.setContext('debug_context', getDebugContext());
};

export const setUser = (
  user: null | { email: string; id: string; username: string },
) => {
  Sentry.setUser(user);
};
